import React from "react";
import { Link } from "@/lib/router-compat";
import { Target, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const TARGETING_IMG =
  "https://vibe.filesafe.space/1787327131284421342/attachments/181ab570-db21-4637-aec8-ec4ab2327b04.png";

export const SaintTargetingComparisonSection: React.FC = () => {
  return (
    <section
      aria-label="Why Personalized Targeting Matters in SAINT® TMS"
      className="py-14 sm:py-20 bg-muted/20 border-b border-border"
    >
      <div className="container-px mx-auto max-w-6xl px-4">
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-12">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 px-3.5 py-1 text-xs sm:text-sm font-bold uppercase tracking-wider text-accent mb-3">
            <Target className="h-4 w-4" />
            fMRI Precision Guidance
          </span>
          <h2 className="font-headings text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primary tracking-tight">
            Why Personalized Targeting Matters
          </h2>
          <p className="mt-3 text-base sm:text-lg text-foreground/80 leading-relaxed">
            Standard TMS uses standard scalp measurements — essentially an
            approximation. SAINT® uses your own brain imaging to pinpoint your
            precise stimulation target.
          </p>
        </div>

        {/* Infographic Guide */}
        <div className="mx-auto max-w-4xl overflow-hidden rounded-3xl border border-border bg-card p-3 sm:p-5 shadow-premium mb-8">
          <img
            src={TARGETING_IMG}
            alt="Why Personalized Targeting Matters: Standard targeting uses a one-size-fits-all approach with broader stimulation area and the same target for everyone; SAINT® personalized targeting is guided by your unique brain with an individualized target and fMRI imaging designed to improve response"
            className="w-full h-auto rounded-2xl object-contain"
            loading="lazy"
            width={1200}
            height={900}
          />
        </div>

        {/* Deep Dive Callout */}
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-soft max-w-4xl mx-auto">
          <div className="grid gap-6 md:grid-cols-2 items-center">
            <div>
              <h3 className="font-headings text-lg sm:text-xl font-bold text-primary">
                Every Brain&apos;s Wiring Is Unique
              </h3>
              <p className="mt-2 text-sm text-foreground/80 leading-relaxed">
                Just as no two fingerprints are identical, your depression
                circuitry communicates through specific connection hubs. Magnus
                Medical&apos;s SNT™ algorithm analyzes resting-state fMRI data
                to locate the subgenual cingulate connectivity node, giving our
                care team the exact coordinates for coil placement.
              </p>
            </div>
            <div className="space-y-2.5 border-t border-border pt-4 md:border-t-0 md:border-l md:pl-6 md:pt-0">
              <div className="flex items-start gap-2 text-sm text-foreground/85">
                <CheckCircle2 className="mt-0.5 h-4 w-4 text-accent shrink-0" />
                <span>
                  Resting-state fMRI scan ordered and coordinated for you
                </span>
              </div>
              <div className="flex items-start gap-2 text-sm text-foreground/85">
                <CheckCircle2 className="mt-0.5 h-4 w-4 text-accent shrink-0" />
                <span>
                  Neuronavigation system tracks coil position in real time
                </span>
              </div>
              <div className="flex items-start gap-2 text-sm text-foreground/85">
                <CheckCircle2 className="mt-0.5 h-4 w-4 text-accent shrink-0" />
                <span>
                  Repeatable precision across all 50 treatment sessions
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
